import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { fetchTags, createTag, updateTag, deleteTag, fetchLastUsedTags } from '@/lib/api'
import { mutationErrorHandler } from '@/lib/utils'
import type { Tag } from '@/lib/api'

export type { Tag } from '@/lib/api'

export function useTags() {
  return useQuery<Tag[]>({
    queryKey: ['tags'],
    queryFn: fetchTags,
  })
}

export function useLastUsedTags() {
  return useQuery<Tag[]>({
    queryKey: ['tags', 'last-used'],
    queryFn: fetchLastUsedTags,
  })
}

export function useCreateTag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createTag,
    onSuccess: () => {
      toast.success('タグを作成しました')
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
    onError: mutationErrorHandler('作成に失敗しました'),
  })
}

export function useUpdateTag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; color: string } }) =>
      updateTag(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['tags'] })
      const previous = queryClient.getQueryData<Tag[]>(['tags'])
      queryClient.setQueryData<Tag[]>(['tags'], (old) =>
        old?.map((t) => (t.id === id ? { ...t, ...data } : t))
      )
      return { previous }
    },
    onSuccess: () => {
      toast.success('タグを更新しました')
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['tags'], context.previous)
      }
      mutationErrorHandler('更新に失敗しました')(error)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
  })
}

export function useDeleteTag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteTag,
    onSuccess: () => {
      toast.success('タグを削除しました')
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
    onError: mutationErrorHandler('削除に失敗しました'),
  })
}
